import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { createClientErrorReference, reportClientError } from "@/lib/client-error-reporting";

export type RuntimeSurfaceContext = "public" | "session" | "app";

interface RuntimeSurfaceBoundaryProps {
  children: ReactNode;
  context: RuntimeSurfaceContext;
  resetKey?: string;
}

interface RuntimeSurfaceBoundaryState {
  hasError: boolean;
  errorReference?: string;
}

const surfaceContent: Record<
  RuntimeSurfaceContext,
  {
    title: string;
    description: string;
    backLabel: string;
    backHref: string;
  }
> = {
  public: {
    title: "A Match teve uma oscilacao ao abrir esta pagina.",
    description:
      "A camada publica continuou protegida. Recarregue agora ou volte para a pagina inicial para seguir navegando.",
    backLabel: "Voltar para a home",
    backHref: "/",
  },
  session: {
    title: "Nao conseguimos abrir sua sessao com seguranca.",
    description:
      "Sua autenticacao nao foi perdida. Recarregue a tela ou volte para o login para retomar o fluxo com seguranca.",
    backLabel: "Voltar para o login",
    backHref: "/auth",
  },
  app: {
    title: "Nao foi possivel carregar seu workspace agora.",
    description:
      "O app entrou em fallback protegido para evitar tela branca. Recarregue agora ou volte para a entrada principal do produto.",
    backLabel: "Ir para o app",
    backHref: "/app",
  },
};

class RuntimeSurfaceBoundaryBase extends Component<
  RuntimeSurfaceBoundaryProps,
  RuntimeSurfaceBoundaryState
> {
  state: RuntimeSurfaceBoundaryState = {
    hasError: false,
    errorReference: undefined,
  };

  static getDerivedStateFromError(_error: unknown): RuntimeSurfaceBoundaryState {
    return {
      hasError: true,
      errorReference: createClientErrorReference("runtime"),
    };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    reportClientError(
      `runtime-boundary:${this.props.context}`,
      error,
      import.meta.env.DEV
        ? { componentStack: errorInfo.componentStack || "unavailable" }
        : undefined,
    );
  }

  componentDidUpdate(prevProps: RuntimeSurfaceBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({
        hasError: false,
        errorReference: undefined,
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleBack = () => {
    window.location.assign(surfaceContent[this.props.context].backHref);
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const copy = surfaceContent[this.props.context];

    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background px-4 py-10 sm:px-6"
        data-testid="runtime-surface-boundary"
        data-surface-context={this.props.context}
      >
        <div className="w-full max-w-xl rounded-[28px] border border-border bg-card/95 p-6 shadow-[0_24px_90px_rgba(15,23,42,0.18)] sm:p-8">
          <div className="mb-5 inline-flex rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.28em] text-emerald-700">
            Match runtime fallback
          </div>

          <div className="space-y-3">
            <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-[2rem]">
              {copy.title}
            </h1>
            <p className="text-sm leading-6 text-muted-foreground sm:text-base">
              {copy.description}
            </p>
          </div>

          <div className="mt-6 rounded-2xl border border-border/70 bg-muted/30 p-4">
            <p className="text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Estado protegido
            </p>
            <p className="mt-2 text-sm text-foreground">
              Erro de runtime capturado sem derrubar a interface inteira.
            </p>
            {this.state.errorReference ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Referencia tecnica: {this.state.errorReference}
              </p>
            ) : null}
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex min-h-12 flex-1 items-center justify-center rounded-2xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition hover:opacity-95"
            >
              Recarregar agora
            </button>
            <button
              type="button"
              onClick={this.handleBack}
              className="inline-flex min-h-12 flex-1 items-center justify-center rounded-2xl border border-border bg-background px-4 py-3 text-sm font-semibold text-foreground transition hover:bg-muted/40"
            >
              {copy.backLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export const RuntimeSurfaceBoundary = RuntimeSurfaceBoundaryBase;

export function RouteRuntimeBoundary({
  children,
  context,
}: Omit<RuntimeSurfaceBoundaryProps, "resetKey">) {
  const location = useLocation();

  return (
    <RuntimeSurfaceBoundaryBase
      context={context}
      resetKey={`${location.pathname}${location.search}${location.hash}`}
    >
      {children}
    </RuntimeSurfaceBoundaryBase>
  );
}
